import React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import AutoSizer, { Size } from 'react-virtualized-auto-sizer';
import isEqual from 'lodash.isequal';

import { AppState } from 'services/types';
import {
    VirtualizedTableColumnKey,
    VirtualizedTableColumnWidth,
    VirtualizedTableColumns,
    VirtualizedTableItem
} from 'services/virtualizedTable/types';
import { setColumnWidth } from 'services/columnWidths/actions';

import VirtualizedTable from 'components/VirtualizedTable';

import {
    requestCreateCommodity,
    requestDeleteCommodity,
    requestUpdateCommodity
} from 'services/commodities/actions';

// @ts-ignore
import { selectCurrentOrganizationId } from 'services/currentOrganizationId/selectors';

import { CommodityCreatePayload } from 'services/commodities/types';
import {
    selectCommoditiesItems,
    selectCommoditiesTableColumns
} from './selectors';

interface OwnProps {
    tableId: string;
}

interface ConnectedProps {
    items: VirtualizedTableItem[];
    columns: VirtualizedTableColumns;
    currentOrganizationId: string;
}

interface DispatchProps {
    setColumnWidth: typeof setColumnWidth;
    requestCreateCommodity: typeof requestCreateCommodity;
    requestUpdateCommodity: typeof requestUpdateCommodity;
    requestDeleteCommodity: typeof requestDeleteCommodity;
}

type Props = OwnProps & ConnectedProps & DispatchProps;

export class CommoditiesVirtualizedTable extends React.Component<Props> {
    shouldComponentUpdate(nextProps: Props): boolean {
        return !isEqual(this.props, nextProps);
    }

    handleCreate = (item: VirtualizedTableItem): void => {
        const {
            requestCreateCommodity: createCommodity,
            currentOrganizationId
        } = this.props;

        const payload: CommodityCreatePayload = {
            name: item.name,
            organizationId: currentOrganizationId
        };

        createCommodity(payload);
    };

    handleColumnResized = (
        columnKey: VirtualizedTableColumnKey,
        columnWidth: VirtualizedTableColumnWidth
    ): void => {
        const { tableId, ...props } = this.props;
        props.setColumnWidth(tableId, columnKey, columnWidth);
    };

    handleItemUpdate = (item: VirtualizedTableItem): void => {
        console.warn('missing update!');
    };

    handleItemDelete = (items: VirtualizedTableItem): void => {
        const { requestDeleteCommodity: deleteCommodity } = this.props;

        if (items[0] && items[0].id) {
            deleteCommodity({ id: items[0].id });
        }
    };

    renderVirtualizedTable = ({ height, width }: Size): React.ReactElement => {
        const { tableId, items, columns } = this.props;

        return (
            <VirtualizedTable
                id={tableId}
                height={height}
                width={width}
                columns={columns}
                items={items}
                onColumnResized={this.handleColumnResized}
                label="Commodity"
                labelPlural="Commodities"
                enableItemEditing
                onItemUpdate={this.handleItemUpdate}
                enableCreating
                onCreate={this.handleCreate}
                enableDeleting
                selectable
                onDelete={this.handleItemDelete}
            />
        );
    };

    render(): React.ReactElement {
        return (
            <AutoSizer>
                {(size: Size): React.ReactElement =>
                    this.renderVirtualizedTable(size)
                }
            </AutoSizer>
        );
    }
}

const mapStateToProps = createStructuredSelector<
    AppState,
    OwnProps,
    ConnectedProps
>({
    items: selectCommoditiesItems,
    columns: selectCommoditiesTableColumns,
    currentOrganizationId: selectCurrentOrganizationId
});

export default connect(mapStateToProps, {
    setColumnWidth,
    requestCreateCommodity,
    requestUpdateCommodity,
    requestDeleteCommodity
})(CommoditiesVirtualizedTable);
